/**
 * Universal email-suppression admin page.
 *
 * Every address that opted out of ANY cadence (nurture, cold outreach,
 * pilot drip, booking reminder) lives in `db.email_suppressions`. This
 * page lists them all with source-tag chips, one-click unsuppress, and a
 * form to manually add an address (hard-bounce cleanup, explicit blocks).
 *
 * Feeds off:
 *   GET  /api/admin/suppressions
 *   POST /api/admin/suppressions
 *   POST /api/admin/suppressions/{email}/unsuppress
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkle, XCircle, Plus, MagnifyingGlass } from "@phosphor-icons/react";
import client from "../lib/api";

const SOURCE_STYLE = {
  nurture_unsubscribe: "border-sky-400/40 bg-sky-400/[0.06] text-sky-300",
  lead_unsubscribe: "border-amber-400/40 bg-amber-400/[0.06] text-amber-300",
  manual: "border-neutral-600 bg-neutral-800/40 text-neutral-300",
  test: "border-neutral-700 bg-neutral-900 text-neutral-500",
};

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminSuppressionsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await client.get("/admin/suppressions");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load suppressions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addManual = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await client.post("/admin/suppressions", { email: newEmail.trim(), source: "manual" });
      toast.success(`Suppressed ${newEmail.trim()}`);
      setNewEmail("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setAdding(false);
    }
  };

  const unsuppress = async (email) => {
    if (!window.confirm(`Reinstate ${email} into all cadences?`)) return;
    try {
      await client.post(`/admin/suppressions/${encodeURIComponent(email)}/unsuppress`);
      toast.success(`Reinstated ${email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unsuppress failed");
    }
  };

  const filtered = useMemo(() => {
    const list = data?.suppressions || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) => (s.email || "").toLowerCase().includes(q));
  }, [data, search]);

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="admin-suppressions-page">
      <div>
        <div className="overline text-[#FF9764] mb-1 flex items-center gap-1.5">
          <Sparkle size={11} weight="fill" /> Deliverability
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          Email suppressions
        </h1>
        <p className="text-sm text-neutral-500 mt-1 max-w-2xl">
          Everyone here is silenced across <span className="text-neutral-300">every</span> automated cadence — nurture, cold outreach,
          pilot drip, booking reminder, no-show follow-up. One click to reinstate.
        </p>
      </div>

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        <StatCell label="Total" value={data?.total || 0} testid="supp-stat-total" />
        {Object.entries(data?.by_source || {}).slice(0, 3).map(([src, count]) => (
          <StatCell
            key={src}
            label={src.replace(/_/g, " ")}
            value={count}
            testid={`supp-stat-${src}`}
            accent={SOURCE_STYLE[src]}
          />
        ))}
      </div>

      {/* Manual add */}
      <form onSubmit={addManual} className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-3">
        <Plus size={14} weight="bold" className="text-[#FF5A1F] ml-1" />
        <input
          type="email"
          placeholder="Manually add an email to suppress"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          data-testid="supp-add-email"
          className="flex-1 bg-transparent text-sm text-white focus:outline-none placeholder:text-neutral-600"
        />
        <button
          type="submit"
          disabled={adding || !newEmail.trim()}
          data-testid="supp-add-submit"
          className="btn-primary text-xs uppercase tracking-widest rounded-full px-4 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {adding ? "Adding…" : "Add"}
        </button>
      </form>

      {/* Search */}
      <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2">
        <MagnifyingGlass size={14} className="text-neutral-500" />
        <input
          type="text"
          placeholder="Search suppressed emails"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="supp-search"
          className="flex-1 bg-transparent text-sm text-white focus:outline-none placeholder:text-neutral-600"
        />
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 py-12 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 py-12 text-center text-sm text-neutral-500" data-testid="supp-empty">
          {search ? "No suppressed emails match that search." : "Nobody is suppressed yet — clean list."}
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="supp-list">
          <div className="grid grid-cols-[1fr_1fr_120px_120px] gap-3 px-4 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold border-b border-white/5">
            <div>Email</div>
            <div>Sources</div>
            <div>Added</div>
            <div className="text-right">Action</div>
          </div>
          <div className="divide-y divide-white/5 max-h-[560px] overflow-y-auto">
            {filtered.map((s) => (
              <div key={s.email} className="grid grid-cols-[1fr_1fr_120px_120px] gap-3 items-center px-4 py-3 text-sm" data-testid={`supp-row-${s.email}`}>
                <div className="truncate text-white">{s.email}</div>
                <div className="flex flex-wrap gap-1">
                  {(s.sources || []).map((src) => (
                    <span
                      key={src}
                      className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${SOURCE_STYLE[src] || "border-white/10 text-neutral-400"}`}
                    >
                      {src.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
                <div className="text-[11px] text-neutral-500 tabular-nums">{fmt(s.created_at)}</div>
                <div className="text-right">
                  <button
                    onClick={() => unsuppress(s.email)}
                    data-testid={`supp-unsuppress-${s.email}`}
                    className="text-[11px] uppercase tracking-widest text-neutral-400 hover:text-emerald-300 inline-flex items-center gap-1 border border-white/10 hover:border-emerald-400/40 rounded-full px-2.5 py-1"
                  >
                    <XCircle size={11} weight="bold" /> Reinstate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const StatCell = ({ label, value, testid, accent }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5" data-testid={testid}>
    <div className={`text-[10px] uppercase tracking-widest font-semibold mb-1 ${accent ? accent.split(" ")[2] : "text-neutral-500"}`}>
      {label}
    </div>
    <div className="text-2xl font-semibold tabular-nums">{value}</div>
  </div>
);
